import {I18n} from '@aws-amplify/core';

import {FlexiButton} from '@flexinet/ui-components';

import {useRestoreAccountMutation} from '../hooks/useAccountsQuery';
import {useAccountsSelection} from '../hooks/useAccountsSelection.ts';
import {useAccounts} from '../hooks/useAccounts';
import {AiOutlineUndo} from 'react-icons/ai';


// FNP-383: admin Restore button. Flips selected PENDING_DELETION accounts
// back to ACTIVE via the console-api restoreAccount 3-step pipeline (FNP-382)
// which emits the account-restoration-completed EventBridge event consumed
// by workflows-api (FNP-381) to re-enable the Cognito user.
//
// No confirmation modal here — restoration is the reversible direction
// (Cognito re-enable + status flip), unlike Remove which kicks off the
// 30-day cascade. If a confirmation step is wanted later, mirror
// RemoveRecordButton, which hand-rolls modals.openConfirmModal with
// account-specific copy (FNP-956).
export const RestoreRecordButton = () => {

    const restoreAccountMutation = useRestoreAccountMutation();

    const {
        selection,
        handleClearSelection,
    } = useAccountsSelection();

    const {items} = useAccounts();

    const selectedIds = Object.keys(selection).filter(id => selection[id] === true);

    // FNP-986: only PENDING_DELETION accounts are restorable. restoreAccount's
    // ConditionExpression rejects anything else with "precondition failed:
    // account is not in PENDING_DELETION state", so offering Restore on an
    // ACTIVE selection invited the operator into a guaranteed red error.
    //
    // Status comes from the fetched records, not the selection slice: the table
    // dispatches addToSelection({id}) with the id ALONE, so the slice never
    // holds accountStatus. `items` is the React Query cache the table already
    // renders from, so this adds no fetch and cannot disagree with the rows on
    // screen.
    //
    // Mixed selection acts on the restorable subset rather than disabling:
    // selecting 3 rows of which 1 is PENDING_DELETION restores that one, and
    // the label reads "Restore (1)" so the count always matches what will
    // actually happen. Disabling the button instead would leave the operator
    // no way to act without re-selecting, and the count would be the lie.
    const restorableIds = selectedIds.filter(
        id => items.find(item => item.id === id)?.accountStatus === 'PENDING_DELETION'
    );

    const disabled = restorableIds.length === 0;

    const handleRestore = async () => {
        for (const id of restorableIds) {
            try {
                await restoreAccountMutation.mutateAsync(id);
            } catch {
                // notified by the mutation's onError; continue with the rest.
            }
        }
        handleClearSelection();
    };

    // FNP-956: surface the blast radius on the label, matching Remove.
    const label = disabled
        ? I18n.get('Restore')
        : `${I18n.get('Restore')} (${restorableIds.length})`;

    return (<FlexiButton
        leftSection={<AiOutlineUndo/>}
        disabled={disabled}
        onClick={handleRestore}
    >{label}</FlexiButton>);

};
